import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { fetchProbationReviewsMine, ProbationReviewListItem } from "services/probation-review.service";

/**
 * The pending probation reviews the CURRENT user must decide or may see
 * (docs/prd/probation-salary-review.md §5.1, §7.5) — the list on the
 * «Bandomojo laikotarpio vertinimai» queue page of the task list. Unlike the
 * ws-approvals queue this is not gated by a permission group: a plain line
 * manager with no special role can still have reviews waiting (decision 2).
 * Same company-guard pattern as the other queue slices — a stale company's
 * list is never shown under the next one.
 */
export interface ProbationReviewsState {
  companyId: string | null;
  count: number;
  items: ProbationReviewListItem[];
  loaded: boolean;
  isLoading: boolean;
  isError: boolean;
}

const initialState: ProbationReviewsState = {
  companyId: null,
  count: 0,
  items: [],
  loaded: false,
  isLoading: false,
  isError: false,
};

const probationReviewsSlice = createSlice({
  name: "probation-reviews",
  initialState,
  reducers: {
    resetProbationReviews() {
      return initialState;
    },
    setProbationReviewsLoading(state, action: PayloadAction<{ companyId: string }>) {
      if (state.companyId !== action.payload.companyId) {
        state.companyId = action.payload.companyId;
        state.count = 0;
        state.items = [];
        state.loaded = false;
      }
      state.isLoading = true;
      state.isError = false;
    },
    setProbationReviews(state, action: PayloadAction<{ companyId: string; count: number; items: ProbationReviewListItem[] }>) {
      state.companyId = action.payload.companyId;
      state.count = action.payload.count;
      state.items = action.payload.items;
      state.loaded = true;
      state.isLoading = false;
      state.isError = false;
    },
    setProbationReviewsError(state, action: PayloadAction<{ companyId: string }>) {
      state.companyId = action.payload.companyId;
      state.isLoading = false;
      state.isError = true;
    },
  },
});

const { actions, reducer } = probationReviewsSlice;

export const {
  resetProbationReviews,
  setProbationReviewsLoading,
  setProbationReviews,
  setProbationReviewsError,
} = actions;

export default reducer;

// Only the latest request may write — a company switch mid-flight must not
// leave the list at a previous company's answer.
let latestRequest = 0;

/** The whole queue of the current user (§5.1). */
export const fetchProbationReviewsMineThunk = (companyId: string) => async (dispatch: any) => {
  const requestId = ++latestRequest;
  dispatch(setProbationReviewsLoading({ companyId }));
  try {
    const { count, items } = await fetchProbationReviewsMine(companyId);
    if (requestId !== latestRequest) return;
    dispatch(setProbationReviews({ companyId, count, items }));
  } catch (e) {
    if (requestId !== latestRequest) return;
    dispatch(setProbationReviewsError({ companyId }));
  }
};

const PROBATION_TAB_ALWAYS_VISIBLE_GROUPS = ["Master", "Access onboarding"];

type RoleLike = { permissionsgroups?: string[] | null } | null | undefined;

/**
 * The probation reviews queue page (§7.5): open once there is something in the
 * queue (a plain line manager included), or always for HR reach, so the page
 * doesn't close the moment their own queue empties out.
 */
export function canSeeProbationReviewsTab(count: number, role: RoleLike): boolean {
  if (count > 0) return true;
  const groups: string[] = role?.permissionsgroups || [];
  return PROBATION_TAB_ALWAYS_VISIBLE_GROUPS.some((permission) => groups.includes(permission));
}
